import React, { useEffect, useState } from "react";
import { AlertCircle, Heart, LayoutGrid, PackageSearch, Star, Tag } from "lucide-react";
import ItemSection from "./ItemSection";
import { getAllItems, getCategoryItems } from "../../utils/itemSearchApi";

// The ordering page's default (no search typed) view:
//   Favourites (Inventory + Deli items subsections)
//   → Deli items (each AI Menu section)
//   → Inventory (each major_category, AI Menu items merged in by name match)
// Any empty group is simply omitted (spec: "handle empty categories,
// sections, and favourites gracefully").
export default function BrowseView({
  favourites,
  menuSections,
  categories,
  loading,
  error,
  isFavourite,
  onToggleFavourite,
  customerId,
}) {
  // Local copy of `categories` so "View More" can append a page to one
  // category without refetching /items/browse or touching the others.
  const [categoryState, setCategoryState] = useState(categories);
  useEffect(() => setCategoryState(categories), [categories]);

  // `catalogueOffset` (from /items/browse) tracks how many catalogue rows
  // are already shown for this category — separate from items.length,
  // which also counts the AI Menu items merged into page 1.
  // "All items": every uploaded item, 10 at a time. Page 1 loads with the
  // view; "View More" appends the next 10 to the ones already shown.
  const [allItems, setAllItems] = useState([]);
  const [allItemsTotal, setAllItemsTotal] = useState(0);

  useEffect(() => {
    if (!customerId) return undefined;
    const controller = new AbortController();
    getAllItems(customerId, 0, { signal: controller.signal })
      .then(({ items, total }) => {
        setAllItems(items);
        setAllItemsTotal(total);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [customerId]);

  const loadMoreAllItems = async () => {
    const { items: nextItems, total } = await getAllItems(customerId, allItems.length);
    setAllItems((prev) => [...prev, ...nextItems]);
    setAllItemsTotal(total);
  };

  const loadMoreCategory = async (categoryName) => {
    const current = categoryState.find((cat) => cat.name === categoryName);
    const offset = current?.catalogueOffset ?? 0;
    const { items: nextItems } = await getCategoryItems(customerId, categoryName, offset);
    setCategoryState((prev) =>
      prev.map((cat) =>
        cat.name === categoryName
          ? { ...cat, items: [...cat.items, ...nextItems], catalogueOffset: cat.catalogueOffset + nextItems.length }
          : cat
      )
    );
  };

  if (loading) return null; // ItemGrid's own Skeletons cover the loading state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white px-6 py-16 text-center">
        <AlertCircle className="mb-3 h-10 w-10 text-gray-300" aria-hidden="true" />
        <p className="font-medium text-gray-900">Something went wrong</p>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  const hasInventoryFavourites = favourites.inventory.length > 0;
  const hasMenuFavourites = favourites.ai_menu.length > 0;
  const hasMenuSections = menuSections.length > 0;
  const hasCategories = categoryState.length > 0;
  const hasAllItems = allItems.length > 0;
  const isEmpty = !hasInventoryFavourites && !hasMenuFavourites && !hasMenuSections && !hasCategories && !hasAllItems;

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white px-6 py-16 text-center">
        <PackageSearch className="mb-3 h-10 w-10 text-gray-300" aria-hidden="true" />
        <p className="font-medium text-gray-900">No items yet</p>
        <p className="mt-1 text-sm text-gray-500">Check back soon, or use the search bar above.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {(hasInventoryFavourites || hasMenuFavourites) && (
        <div className="space-y-6">
          <h1 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <Heart className="h-4 w-4 fill-rose-500 text-rose-500" /> Favourites
          </h1>
          {hasInventoryFavourites && (
            <ItemSection
              title="Inventory"
              items={favourites.inventory}
              isFavourite={isFavourite}
              onToggleFavourite={onToggleFavourite}
            />
          )}
          {hasMenuFavourites && (
            <ItemSection title="Deli items" icon={Star} items={favourites.ai_menu} />
          )}
        </div>
      )}

      {hasMenuSections && (
        <div className="space-y-6">
          <h1 className="text-base font-semibold text-gray-900">Deli items</h1>
          {menuSections.map((section) => (
            <ItemSection key={section.name} title={section.name} items={section.items} />
          ))}
        </div>
      )}

      {hasCategories && (
        <div className="space-y-6">
          <h1 className="text-base font-semibold text-gray-900">Inventory</h1>
          {categoryState.map((category) => (
            <ItemSection
              key={category.name}
              title={category.name}
              icon={Tag}
              items={category.items}
              total={category.total}
              isFavourite={isFavourite}
              onToggleFavourite={onToggleFavourite}
              onLoadMore={() => loadMoreCategory(category.name)}
            />
          ))}
        </div>
      )}

      {hasAllItems && (
        <div className="space-y-6">
          <h1 className="text-base font-semibold text-gray-900">All items</h1>
          <ItemSection
            title={`Everything in inventory (${allItemsTotal})`}
            icon={LayoutGrid}
            items={allItems}
            total={allItemsTotal}
            isFavourite={isFavourite}
            onToggleFavourite={onToggleFavourite}
            onLoadMore={loadMoreAllItems}
          />
        </div>
      )}
    </div>
  );
}
